import React from 'react';

export interface PageHeaderProps {
  /** Optional element to render before the title (e.g., an avatar or icon) */
  prefix?: React.ReactNode;
  /** Route-level page title (renders as h1) */
  title: React.ReactNode | string;
  /** Optional subtitle or description (use div-friendly content; not wrapped in <p>) */
  subtitle?: React.ReactNode;
  /** Optional primary CTA (Button, Link, etc.) */
  action?: React.ReactNode;
  /** Optional status chip (e.g. "Active", "In Progress") */
  status?: React.ReactNode;
  /** Layout: 'default' (title left, action right) or 'centered' */
  layout?: 'default' | 'centered';
  /** Additional class for the wrapper */
  className?: string;
}

/**
 * Shared page header component for consistent title/subtitle/action/status pattern
 * across client portal routes. Uses design tokens.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  prefix,
  title,
  subtitle,
  action,
  status,
  layout = 'default',
  className = ''
}) => {
  const isCentered = layout === 'centered';

  return (
    <header
      className={`mb-6 ${isCentered ? 'text-center' : 'flex flex-col sm:flex-row sm:items-start sm:justify-between sm:gap-4'} ${className}`}
      aria-labelledby="page-title"
    >
      <div className={`min-w-0 flex-1 flex gap-4 ${isCentered ? 'justify-center flex-col items-center' : ''}`}>
        {prefix && <div className="shrink-0">{prefix}</div>}
        <div className="min-w-0 flex-1">
          <div className={`flex flex-wrap items-center gap-2 sm:gap-3 ${isCentered ? 'justify-center' : ''}`}>
            <h1
              id="page-title"
              className="text-2xl font-bold text-primary-800 tracking-tight truncate"
            >
              {title}
            </h1>
            {status && (
              <span className="inline-flex items-center shrink-0">
                {status}
              </span>
            )}
          </div>
          {subtitle ? (
            <div className="mt-1 text-base text-primary-600 truncate">
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>
      {action && (
        <div className={`mt-4 sm:mt-0 shrink-0 ${isCentered ? 'flex justify-center' : ''}`}>
          {action}
        </div>
      )}
    </header>
  );
};

export default PageHeader;
